import { toast } from "@assetwatch/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { workApiFetch } from "../clients/workClient";
import { useWorkScope } from "../components/WorkScopeProvider";
import type { WorkOrderId } from "../lib/workIds";

const workOrderCommentSchema = z.object({
  id: z.string().uuid(),
  text: z.string(),
  author: z.string(),
  canRemove: z.boolean(),
  createdOn: z.string().datetime(),
  attachments: z.array(
    z.object({
      id: z.string().uuid(),
      fileName: z.string(),
      fileSize: z.number().int().nonnegative(),
      downloadUrl: z.string().url(),
    }),
  ),
});

const commentsResponseSchema = z.object({
  comments: z.array(workOrderCommentSchema),
  attachments: z.array(
    z.object({
      id: z.string().uuid(),
      fileName: z.string(),
      fileSize: z.number().int().nonnegative(),
      author: z.string(),
      createdOn: z.string().datetime(),
      canRemove: z.boolean(),
      downloadUrl: z.string().url(),
      commentId: z.string().uuid().nullable(),
    }),
  ),
});

const workOrderFilesResponseSchema = z.object({
  files: z.array(
    z.object({
      id: z.string().uuid(),
      fileName: z.string(),
      fileSize: z.number().int().nonnegative(),
      author: z.string(),
      createdOn: z.string().datetime(),
      canRemove: z.boolean(),
      downloadUrl: z.string().url(),
    }),
  ),
});

const createResponseSchema = z.object({ comment: workOrderCommentSchema });
const removeCommentResponseSchema = z.object({ commentId: z.string().uuid() });
const fileResponseSchema = z.object({ fileId: z.string().uuid() });
const uploadSessionResponseSchema = z.object({
  uploadId: z.string().uuid(),
  uploadUrl: z.string().url().nullable(),
  isUploaded: z.boolean(),
  expiresIn: z.number(),
});
const completeUploadResponseSchema = z.object({
  uploadId: z.string().uuid(),
  uploadStatus: z.string(),
  scanStatus: z.string(),
});

export type WorkOrderComment = z.infer<typeof workOrderCommentSchema>;
export type WorkOrderFile = z.infer<
  typeof workOrderFilesResponseSchema
>["files"][number];

/**
 * Download URLs are presigned for 300 seconds; treating the list as stale
 * after 240 seconds leaves a margin so a refetch replaces them before they
 * expire.
 */
export const workOrderFilesStaleTime = 240_000;

/** A Work Order comment or attachment request the API rejected. */
export class WorkOrderCommentsRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "WorkOrderCommentsRequestError";
  }
}

type WorkOrderCommentsScope = {
  customerId: string;
  facilityId: string;
  workOrderId: WorkOrderId;
};

export type WorkOrderFileUploadInput = {
  uploadId: string;
  fileName: string;
  contentType: string;
  contentLength: number;
  checksumSha256: string;
};

function workOrderPath({
  customerId,
  facilityId,
  workOrderId,
}: WorkOrderCommentsScope) {
  return `/v1/customers/${encodeURIComponent(customerId)}/facilities/${encodeURIComponent(facilityId)}/work-orders/${encodeURIComponent(workOrderId)}`;
}

function commentsPath(scope: WorkOrderCommentsScope) {
  return `${workOrderPath(scope)}/comments`;
}

async function errorMessage(response: Response) {
  const body: unknown = await response.json().catch(() => null);
  if (
    typeof body === "object" &&
    body !== null &&
    "error" in body &&
    typeof body.error === "string"
  )
    return body.error;
  return `Work Order comments request failed (${response.status}).`;
}

async function requestJson<T>(
  path: string,
  schema: z.ZodType<T>,
  init?: RequestInit,
) {
  const response = await workApiFetch(path, init);
  if (!response.ok)
    throw new WorkOrderCommentsRequestError(
      await errorMessage(response),
      response.status,
    );
  return schema.parse(await response.json());
}

function postJson(body: unknown): RequestInit {
  return {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  };
}

export function fetchWorkOrderComments(scope: WorkOrderCommentsScope) {
  return requestJson(commentsPath(scope), commentsResponseSchema);
}

/** Files attached directly to the Work Order, excluding comment attachments. */
export function fetchWorkOrderFiles(scope: WorkOrderCommentsScope) {
  return requestJson(
    `${workOrderPath(scope)}/files`,
    workOrderFilesResponseSchema,
  );
}

export function createWorkOrderComment(
  scope: WorkOrderCommentsScope,
  input: { submissionId: string; text: string; uploadIds: string[] },
) {
  return requestJson(
    commentsPath(scope),
    createResponseSchema,
    postJson(input),
  );
}

export function removeWorkOrderComment(
  scope: WorkOrderCommentsScope,
  commentId: string,
) {
  return requestJson(
    `${commentsPath(scope)}/${encodeURIComponent(commentId)}`,
    removeCommentResponseSchema,
    { method: "DELETE" },
  );
}

export function removeWorkOrderFile(
  scope: WorkOrderCommentsScope,
  input: { fileId: string; commentId: string | null },
) {
  const owner = input.commentId
    ? `${commentsPath(scope)}/${encodeURIComponent(input.commentId)}`
    : workOrderPath(scope);
  return requestJson(
    `${owner}/files/${encodeURIComponent(input.fileId)}`,
    fileResponseSchema,
    { method: "DELETE" },
  );
}

export function createWorkOrderFileUpload(
  scope: WorkOrderCommentsScope,
  input: WorkOrderFileUploadInput,
) {
  return requestJson(
    `${workOrderPath(scope)}/upload-url`,
    uploadSessionResponseSchema,
    postJson(input),
  );
}

export function retryWorkOrderFileUpload(
  scope: WorkOrderCommentsScope,
  uploadId: string,
) {
  return requestJson(
    `${workOrderPath(scope)}/upload-url/retry`,
    uploadSessionResponseSchema,
    postJson({ uploadId }),
  );
}

export function completeWorkOrderFileUpload(
  scope: WorkOrderCommentsScope,
  uploadId: string,
) {
  return requestJson(
    `${workOrderPath(scope)}/upload-url/completion`,
    completeUploadResponseSchema,
    postJson({ uploadId }),
  );
}

export function attachWorkOrderFile(
  scope: WorkOrderCommentsScope,
  uploadId: string,
) {
  return requestJson(
    `${workOrderPath(scope)}/files`,
    fileResponseSchema,
    postJson({ uploadId }),
  );
}

export function useWorkOrderCommentsQuery(workOrderId: WorkOrderId) {
  const { selectedCustomer, selectedFacility } = useWorkScope();
  const customerId = selectedCustomer?.id;
  const facilityId = selectedFacility?.id;

  return useQuery({
    queryKey: ["work-order-comments", customerId, facilityId, workOrderId],
    queryFn: () => {
      if (!customerId || !facilityId)
        throw new Error("A customer and facility must be selected.");
      return fetchWorkOrderComments({ customerId, facilityId, workOrderId });
    },
    enabled: Boolean(customerId && facilityId && workOrderId),
    meta: { errorMessage: "Unable to load Work Order comments." },
  });
}

export function useWorkOrderFilesQuery(workOrderId: WorkOrderId) {
  const { selectedCustomer, selectedFacility } = useWorkScope();
  const customerId = selectedCustomer?.id;
  const facilityId = selectedFacility?.id;

  return useQuery({
    queryKey: ["work-order-files", customerId, facilityId, workOrderId],
    queryFn: () => {
      if (!customerId || !facilityId)
        throw new Error("A customer and facility must be selected.");
      return fetchWorkOrderFiles({ customerId, facilityId, workOrderId });
    },
    enabled: Boolean(customerId && facilityId && workOrderId),
    staleTime: workOrderFilesStaleTime,
  });
}

export function useWorkOrderCommentsMutations(workOrderId: WorkOrderId) {
  const { selectedCustomer, selectedFacility } = useWorkScope();
  const queryClient = useQueryClient();
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["work-order-comments"] });
  // File changes also change the Work Order's attachment list.
  const invalidateFiles = () =>
    Promise.all([
      invalidate(),
      queryClient.invalidateQueries({ queryKey: ["work-order-files"] }),
    ]);
  function scope(): WorkOrderCommentsScope {
    if (!selectedCustomer || !selectedFacility)
      throw new Error("A customer and facility must be selected.");
    return {
      customerId: selectedCustomer.id,
      facilityId: selectedFacility.id,
      workOrderId,
    };
  }

  const create = useMutation({
    mutationFn: (input: {
      submissionId: string;
      text: string;
      uploadIds: string[];
    }) => createWorkOrderComment(scope(), input),
    onSuccess: () => {
      invalidate();
      // The comment box shows the failure inline, so only success toasts here.
      toast.add("Comment added.", { icon: true });
    },
  });
  const remove = useMutation({
    mutationFn: (commentId: string) =>
      removeWorkOrderComment(scope(), commentId),
    onSuccess: () => {
      invalidate();
      toast.add("Comment removed.", { icon: true });
    },
    onError: (error: Error) => toast.error(error.message, { icon: true }),
  });
  const removeFile = useMutation({
    mutationFn: (input: { fileId: string; commentId: string | null }) =>
      removeWorkOrderFile(scope(), input),
    onSuccess: () => {
      void invalidateFiles();
      toast.add("Attachment removed.", { icon: true });
    },
    onError: (error: Error) => toast.error(error.message, { icon: true }),
  });
  const attachFile = useMutation({
    mutationFn: (uploadId: string) => attachWorkOrderFile(scope(), uploadId),
    onSuccess: () => {
      void invalidateFiles();
      // The attachments section shows the failure inline with a retry action.
      toast.add("Attachment added.", { icon: true });
    },
  });

  // Upload staging steps do not change cached data, so they are plain client
  // calls; the comment or attach mutation that follows owns the feedback.
  return {
    create,
    remove,
    removeFile,
    attachFile,
    createUpload: (input: WorkOrderFileUploadInput) =>
      createWorkOrderFileUpload(scope(), input),
    retryUpload: (input: { uploadId: string }) =>
      retryWorkOrderFileUpload(scope(), input.uploadId),
    completeUpload: (uploadId: string) =>
      completeWorkOrderFileUpload(scope(), uploadId),
  };
}
